const PROJECT_SIZE_LIMIT = 600_000;
const PROGRESS_RING_RADIUS = 8;
const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RING_RADIUS;

interface ProjectSizePillProps {
  inputTokens: number;
}

const formatInputTokens = (inputTokens: number) => {
  return Math.round(inputTokens).toLocaleString('en-US');
};

const getConsumptionColorClass = (inputTokens: number) => {
  if (inputTokens >= 400_000) {
    return 'text-red-400';
  }

  if (inputTokens >= 300_000) {
    return 'text-yellow-400';
  }

  return 'text-green-400';
};

export function ProjectSizePill({ inputTokens }: ProjectSizePillProps) {
  const progress = Math.min(Math.max(inputTokens / PROJECT_SIZE_LIMIT, 0), 1);
  const progressOffset = PROGRESS_RING_CIRCUMFERENCE * (1 - progress);

  return (
    <div
      className="hidden sm:flex items-center gap-2.5 rounded-md border px-2 py-1 transition-colors duration-300"
      style={{
        backgroundColor: 'var(--dualite-background-button)',
        borderColor: 'var(--dualite-border-primary)',
        boxShadow: 'var(--dualite-shadow-sm)',
      }}
      title="Updated after each completed request"
      aria-label={`Project size: ${formatInputTokens(inputTokens)} tokens`}
    >
      <span
        className={`relative flex h-6 w-6 shrink-0 items-center justify-center ${getConsumptionColorClass(inputTokens)}`}
        aria-hidden="true"
      >
        <svg className="h-6 w-6 -rotate-90" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r={PROGRESS_RING_RADIUS} stroke="var(--dualite-border-secondary)" strokeWidth="2" />
          <circle
            cx="10"
            cy="10"
            r={PROGRESS_RING_RADIUS}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={PROGRESS_RING_CIRCUMFERENCE}
            strokeDashoffset={progressOffset}
            className="transition-[stroke-dashoffset] duration-500 ease-out"
          />
        </svg>
        <span className="absolute h-1 w-1 rounded-full bg-current opacity-60" />
      </span>

      <span className="flex flex-col whitespace-nowrap">
        <span
          style={{
            color: 'var(--dualite-text-primary)',
            fontFamily: 'var(--dualite-font-family-body)',
            fontSize: '10px',
            fontWeight: 'var(--dualite-font-weight-medium)',
            lineHeight: 'var(--dualite-line-height-tight)',
          }}
        >
          Project Size
        </span>
        <span
          className="origin-left scale-90 tabular-nums"
          style={{
            color: 'var(--dualite-text-secondary)',
            fontFamily: 'var(--dualite-font-family-body)',
            fontSize: '10px',
            fontWeight: 'var(--dualite-font-weight-regular)',
            lineHeight: 'var(--dualite-line-height-tight)',
          }}
        >
          {formatInputTokens(inputTokens)} tokens
        </span>
      </span>
    </div>
  );
}
